import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Autocomplete,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  IconButton,
} from "@mui/material";
import {
  AddCircleOutlineRounded,
  AddCircleOutline,
  Close,
} from "@mui/icons-material";
import { keyframes } from "@mui/system";
import { useNavigate } from "react-router-dom";
import { adminService } from "../services/adminService";
import {
  ROUTES,
  buttonGradient,
  buttonGradientHover,
  buttonShadow,
} from "../../../constants/consts";

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const USER_TYPES = [
  { value: "all", label: "Herkes" },
  { value: "user", label: "Kullanıcı" },
  { value: "rootUser", label: "Admin Müşteri" },
];

const userTypeLabel = (v) => USER_TYPES.find((t) => t.value === v)?.label || v;

const normalize = (s = "") => String(s).toLocaleLowerCase("tr-TR").trim();

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    transition: "box-shadow 0.25s ease, background-color 0.25s ease",
    "&:hover": { backgroundColor: "rgba(4,87,129,0.02)" },
    "&.Mui-focused": { boxShadow: "0 0 0 4px rgba(4,87,129,0.10)" },
  },
};

const EMPTY = {
  title: "",
  description: "",
  sunucuKodu: "Hepsi",
  userType: "all",
};

export default function AdminNotificationAdd() {
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [sunucuKoduList, setSunucuKoduList] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [snack, setSnack] = useState({ open: false, msg: "" });
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const companies = await adminService.getCompanies();
        const list = [
          "Hepsi",
          ...companies.map((c) => c.sunucu_kodu).filter(Boolean),
        ];
        setSunucuKoduList(list);
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingOptions(false);
      }
    })();
  }, []);

  const handleChange = (k) => (e) =>
    setForm((p) => ({ ...p, [k]: e.target.value }));

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) {
      setError("Konu ve duyuru zorunludur.");
      return;
    }
    setSaving(true);
    setError("");

    try {
      let finalImage = "";
      if (file) {
        setUploading(true);
        const res = await adminService.uploadFile(
          file,
          "Nontification",
          "image",
        );
        setUploading(false);
        finalImage = res?.url || "";
      }

      await adminService.addNotification({
        title: form.title.trim(),
        description: form.description.trim(),
        image: finalImage,
        sunucuKodu: form.sunucuKodu,
        userType: form.userType,
      });

      setSnack({ open: true, msg: "Duyuru başarıyla eklendi." });
      setForm(EMPTY);
      setFile(null);
      setPreviewUrl("");
      setTimeout(() => navigate(ROUTES.ADMIN_NOTIFICATIONS), 900);
    } catch (err) {
      console.error(err);
      setError("Duyuru eklenemedi.");
    } finally {
      setSaving(false);
      setUploading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        gap: 1.5,
      }}
    >
      {/* Başlık */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          flexShrink: 0,
          animation: `${fadeInUp} 0.4s ease-out both`,
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255,255,255,0.14)",
            border: "1px solid rgba(255,255,255,0.20)",
          }}
        >
          <AddCircleOutlineRounded sx={{ color: "#fff", fontSize: 20 }} />
        </Box>
        <Box>
          <Typography
            variant="h6"
            fontWeight={800}
            color="#fff"
            sx={{ fontSize: 18 }}
          >
            Yeni Duyuru Ekle
          </Typography>
          <Typography
            sx={{
              color: "rgba(255,255,255,0.7)",
              fontSize: 11.5,
              fontWeight: 600,
            }}
          >
            Kullanıcılarınıza yeni bir duyuru gönderin
          </Typography>
        </Box>
      </Box>

      {/* Form */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          overflowY: "auto",
          pt: 1,
          pb: 2,
        }}
      >
        <Paper
          elevation={0}
          component="form"
          onSubmit={handleSubmit}
          sx={{
            p: { xs: 2.5, sm: 4 },
            borderRadius: 3,
            backgroundColor: "rgba(255,255,255,0.98)",
            boxShadow: "0 12px 32px rgba(0,0,0,0.20)",
            border: "1px solid rgba(255,255,255,0.30)",
            width: "100%",
            maxWidth: { xs: "100%", sm: 900, md: 1100 },
            animation: `${fadeInUp} 0.5s cubic-bezier(0.22, 1, 0.36, 1) 0.1s backwards`,
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
              {error}
            </Alert>
          )}

          {/* Resim */}
          <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
            <Box
              sx={{
                position: "relative",
                width: 160,
                height: 160,
                borderRadius: 2,
                backgroundColor: "#F8FAFC",
                border: "2px dashed rgba(4,87,129,0.20)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              {previewUrl ? (
                <Box
                  component="img"
                  src={previewUrl}
                  alt="preview"
                  sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                <Box
                  sx={{
                    textAlign: "center",
                    color: "#94A3B8",
                    fontSize: 12,
                  }}
                >
                  <AddCircleOutline
                    sx={{ fontSize: 32, mb: 1, opacity: 0.5 }}
                  />
                  <div>Resim ekle</div>
                </Box>
              )}

              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handleFile}
              />

              <IconButton
                size="small"
                onClick={() => fileRef.current?.click()}
                disabled={saving}
                sx={{
                  position: "absolute",
                  bottom: 6,
                  right: 6,
                  backgroundColor: "rgba(255,255,255,0.95)",
                  color: "#15803D",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                  "&:hover": { backgroundColor: "#fff" },
                }}
              >
                <AddCircleOutline sx={{ fontSize: 20 }} />
              </IconButton>

              {previewUrl && (
                <IconButton
                  size="small"
                  onClick={() => {
                    setFile(null);
                    setPreviewUrl("");
                  }}
                  sx={{
                    position: "absolute",
                    top: 6,
                    right: 6,
                    backgroundColor: "rgba(255,255,255,0.95)",
                    color: "#B91C1C",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                    "&:hover": { backgroundColor: "#fff" },
                  }}
                >
                  <Close sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: { xs: 2, sm: 2.5 },
            }}
          >
            <TextField
              label="Konu *"
              value={form.title}
              onChange={handleChange("title")}
              fullWidth
              sx={{ ...inputSx, gridColumn: { xs: "1", sm: "1 / span 2" } }}
            />
            <TextField
              label="Duyuru *"
              value={form.description}
              onChange={handleChange("description")}
              fullWidth
              multiline
              minRows={5}
              maxRows={12}
              sx={{ ...inputSx, gridColumn: { xs: "1", sm: "1 / span 2" } }}
            />
            <Autocomplete
              size="small"
              options={sunucuKoduList}
              value={form.sunucuKodu}
              onChange={(_, v) =>
                setForm((p) => ({ ...p, sunucuKodu: v || "Hepsi" }))
              }
              disabled={loadingOptions}
              filterOptions={(opts, { inputValue }) =>
                opts.filter((o) => normalize(o).includes(normalize(inputValue)))
              }
              noOptionsText="Sunucu bulunamadı"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Sunucu Kodu *"
                  placeholder="Yaz veya seç"
                  sx={inputSx}
                />
              )}
            />
            <Autocomplete
              size="small"
              options={USER_TYPES.map((t) => t.value)}
              value={form.userType}
              onChange={(_, v) =>
                setForm((p) => ({ ...p, userType: v || "all" }))
              }
              getOptionLabel={userTypeLabel}
              disableClearable
              renderOption={(props, opt) => (
                <li {...props}>{userTypeLabel(opt)}</li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Kullanıcı Türü *" sx={inputSx} />
              )}
            />
          </Box>

          <Box
            sx={{
              mt: 4,
              display: "flex",
              gap: 1.5,
              justifyContent: "flex-end",
            }}
          >
            <Button
              type="button"
              variant="outlined"
              onClick={() => navigate(ROUTES.ADMIN_NOTIFICATIONS)}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                px: 3,
                py: 1.2,
                fontWeight: 600,
              }}
            >
              Vazgeç
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving || uploading}
              sx={{
                textTransform: "none",
                borderRadius: 2,
                fontWeight: 700,
                px: 4,
                py: 1.2,
                background: buttonGradient,
                boxShadow: buttonShadow,
                "&:hover": { background: buttonGradientHover },
                "&:disabled": { background: "#c7c7c7" },
              }}
            >
              {saving || uploading ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                "Kaydet"
              )}
            </Button>
          </Box>
        </Paper>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={2500}
        onClose={() => setSnack({ open: false, msg: "" })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" sx={{ borderRadius: 2 }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
